/* [SAGE-JADE 2026-09-28, user] Two palettes, one per theme (user: "for the light theme use Sage and
   Jade and for the dark theme use Graphite and Emerald"). Both replace Tailwind SLATE (a blue-grey that
   colour systems pair with blue brands; under the green brand it read as generic grey).
   LIGHT = "Sage & Jade": Radix sage greys (a faint green undertone) + the jade brand greens
     (green600/green700 #1a7f64 / #146b54, unchanged).
   DARK = "Graphite & Emerald": pure neutral greys (no tint, Supabase / Vercel style) + emerald greens
     at Supabase's brand hue (oklch h 157.5); green600/green700 are emerald in dark.
   Hard-coded literals in components follow the same split: slate/blue-grey moved to the new hue at the
   SAME lightness (sage in light-only code, neutral in dark-only code) and greens to jade / emerald,
   so contrasts tuned before still hold. Colours shared by both themes were made theme-aware by hand.
   New tokens: `raised` (menus, popovers — a step lighter than cards in dark so they don't melt into
   them), `focus` (keyboard focus ring). `danger` is now per theme: #dc2626 was 3.0:1 as text on
   the dark card, below the 4.5:1 floor. */
export const lightTheme = {
  colorScheme: 'light' as const,
  bg: '#eef1f0',
  surface: '#ffffff',
  raised: '#ffffff',
  /* [FULL-INK 2026-09-01, user] `muted` was the app's dim-text grey (#475569 / #cbd5e1) and the
     user asked for NO dim text anywhere outside the PM Singapore board (which never reads this
     token): muted is now full ink — black on light, white on dark — which retints all ~190
     secondary-text sites in one move. The old grey survives as `dim`, reserved for the few
     things that MUST stay grey to keep their meaning: input placeholders (full-ink placeholder
     reads as typed text). Do not put `dim` on ordinary secondary text — that undoes the ask.
     [SAGE-JADE] Full ink is now sage-12 #1a211e (16.4:1 on white) — still full ink, never grey. */
  muted: '#1a211e',
  dim: '#5f6563',
  text: '#1a211e',
  pill: '#e6e9e8',
  line: '#d7dad9',
  green700: '#146b54',
  green600: '#1a7f64',
  /* [GREEN-ON-DARK 2026-08-27] Green used as TEXT (or a text-like SVG fill), as opposed to a
     fill or a border. green600 is a light-theme ink: on the dark surface it lands near 2:1,
     well under the 4.5:1 small text needs, and the user called it out twice ("too dark and not
     visible properly"). The PM Recommendations tab fixed it locally first with exactly these
     values; this token is that fix made app-wide, so a new green label anywhere reads
     `theme.greenInk` and is right in both themes without a ternary at the site. `greenTint` is
     the matching soft background for a green pill.
     [SAGE-JADE] Light is a notch darker than green600 (#16735a) so green text also clears 4.5:1
     on the grey page and on pills (green600 was 4.2:1 there), not only on white cards (5.8:1). */
  greenInk: '#16735a',
  greenTint: 'rgba(41,163,131,0.12)',
  /* [GREEN-PANEL 2026-09-03, user] Green used as a LARGE FILL — a whole card, a table header, a
     total row — as opposed to ink (greenInk) or a pill (greenTint).
     ⚠ THE PROBLEM THIS SOLVES: green600/green700 are BYTE-IDENTICAL in both themes, so a green
     panel that reads as a pleasant accent on white renders at the same saturation on the dark
     ground, where it glares. The user: "in the dark mode it is looking too bright... in the light
     mode it is good." Dark drops to a deep green that sits ON the dark ground.
     [SAGE-JADE] Light is a deeper jade (#125f4b / #0f4f3e) so white on the band reaches 7.6:1
     (green600 was 4.9:1). Dark #1b5745 sits ~1.9:1 off the #202221 card; white on it 8.4:1. */
  greenPanel: '#125f4b',
  greenPanelDeep: '#0f4f3e',
  greenPanelShadow: 'rgba(18,95,75,0.24)',
  radius: '16px',
  shadow: '0 1px 0 rgba(26,33,30,0.03), 0 8px 32px rgba(26,33,30,0.08)',
  shadowHover: '0 25px 50px rgba(26,33,30,0.15)',
  gradientBg: 'linear-gradient(180deg,#f7f9f8,#eef1f0)',
  navActive: 'linear-gradient(180deg,#e6f4ef,#d9f0e8)',
  buttonHover: '#eef1f0',
  buttonActive: '#dfe2e0',
  borderHover: '#b8bcba',
  dateLabel: '#3a423f',
  danger: '#c81e1e',
  focus: '#29a383',
};

export const darkTheme = {
  colorScheme: 'dark' as const,
  /* [GRAPHITE-EMERALD 2026-09-28] Dark = "Graphite & Emerald": achromatic greys (Vercel Geist ramp)
     and emerald greens at Supabase's hue. Near-black page, #1c1c1c cards, menus a step lighter. */
  bg: '#0c0c0c',
  surface: '#1c1c1c',
  raised: '#262626',
  /* See lightTheme.muted — full ink, with `dim` keeping the old grey for placeholders. */
  muted: '#fafafa',
  dim: '#a0a0a0',
  text: '#fafafa',
  pill: '#262626',
  line: '#2e2e2e',
  /* Emerald brand pair for dark (light keeps jade #1a7f64 / #146b54); white on #0b6640 holds 7.0:1. */
  green700: '#0b6640',
  green600: '#0e7e4e',
  /* See lightTheme.greenInk. Emerald #4acd8b (Supabase's dark primary) replaces mint #34d399 as the
     app's dark accent (status dots, table highlights, modals); 8.4:1 on the #1c1c1c card. The
     component literals that repeated #34d399 were moved to this same value. */
  greenInk: '#4acd8b',
  greenTint: 'rgba(62,207,142,0.12)',
  /* See lightTheme.greenPanel. Deep emerald so a full-card fill reads as a green PANEL on the dark
     ground rather than a lit block: #005731 sits ~1.95:1 off the card, white on it 8.7:1. The shadow
     has no green glow — a coloured halo under a dark panel is the other half of what read as "bright". */
  greenPanel: '#005731',
  greenPanelDeep: '#003e22',
  greenPanelShadow: 'rgba(0,0,0,0.38)',
  radius: '16px',
  /* A 1px top highlight carries the edge on dark, where a plain shadow is invisible. */
  shadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.5)',
  shadowHover: '0 25px 50px rgba(0,0,0,0.6)',
  gradientBg: 'linear-gradient(180deg,#1c1c1c,#232323)',
  navActive: 'linear-gradient(180deg,rgba(62,207,142,0.18),rgba(0,87,49,0.2))',
  buttonHover: '#2a2a2a', /* lighter than both the card and `raised`, so a hover always lifts */
  buttonActive: '#2e2e2e',
  borderHover: '#454545',
  dateLabel: '#d4d4d4',
  danger: '#f87171',
  focus: '#3ecf8e',
};

export type ThemeType = Omit<typeof lightTheme, 'colorScheme'> & {
  colorScheme: 'light' | 'dark';
  /** [PURE-INK 2026-10-15] Set only by components/PureInk (pages the user wants at full contrast): the table header labels' ink
   *  (DataTable falls back to its own green when absent) and a deeper green fill for buttonPrimary in the light theme. */
  tableHeadInk?: string;
  primaryDeep?: boolean;
};
